/**
 * Create / edit a catalog item (BON-161). Renders the category-driven
 * AttributeForm, validates against the active definitions client-side (mirror
 * of the backend rules), and POSTs/PATCHes via the catalog API.
 */
import { useEffect, useState } from 'react';

import {
  CATALOG_CATEGORIES,
  createCatalogItem,
  updateCatalogItem,
  fetchAttributeDefinitions,
  type AttributeDefinition,
  type AttributeValues,
  type CatalogItem,
} from '../../../api/inventory-catalog';
import { validateAttributes } from '../../../lib/validateAttributes';

import AttributeForm from './AttributeForm';
import ModalShell from './ModalShell';

interface Props {
  /** Existing item to edit, or null to create a new one. */
  item: CatalogItem | null;
  onClose: () => void;
  onSaved: () => void;
}

const inputClass =
  'mt-1 block w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-moss-500';

interface FieldsProps {
  editing: boolean;
  category: string;
  name: string;
  sku: string;
  isConsumable: boolean;
  attributes: AttributeValues;
  defs: AttributeDefinition[];
  fieldErrors: Record<string, string>;
  onCategory: (v: string) => void;
  onName: (v: string) => void;
  onSku: (v: string) => void;
  onConsumable: (v: boolean) => void;
  onAttributes: (v: AttributeValues) => void;
}

function ItemFields(p: FieldsProps) {
  return (
    <div className="flex flex-col gap-3">
      <label className="block">
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Category</span>
        <select
          className={inputClass}
          value={p.category}
          disabled={p.editing}
          onChange={(e) => p.onCategory(e.target.value)}
        >
          {CATALOG_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Name</span>
        <input
          className={inputClass}
          value={p.name}
          onChange={(e) => p.onName(e.target.value)}
          autoFocus
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
          SKU <span className="text-stone-400">(auto-generated if blank)</span>
        </span>
        <input className={inputClass} value={p.sku} onChange={(e) => p.onSku(e.target.value)} />
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={p.isConsumable}
          onChange={(e) => p.onConsumable(e.target.checked)}
        />
        <span className="text-sm text-stone-700 dark:text-stone-300">Consumable</span>
      </label>
      <div className="border-t border-stone-200 dark:border-stone-700 pt-3">
        <AttributeForm
          definitions={p.defs}
          values={p.attributes}
          errors={p.fieldErrors}
          onChange={p.onAttributes}
        />
      </div>
    </div>
  );
}

export default function ItemFormModal({ item, onClose, onSaved }: Props) {
  const editing = item !== null;
  const [category, setCategory] = useState(item?.category ?? CATALOG_CATEGORIES[0]);
  const [name, setName] = useState(item?.name ?? '');
  const [sku, setSku] = useState(item?.sku ?? '');
  const [isConsumable, setIsConsumable] = useState(item?.isConsumable ?? true);
  const [attributes, setAttributes] = useState<AttributeValues>(item?.attributes ?? {});
  const [defs, setDefs] = useState<AttributeDefinition[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAttributeDefinitions(category)
      .then(setDefs)
      .catch(() => setDefs([]));
  }, [category]);

  async function handleSave() {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    const result = validateAttributes(defs, attributes);
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    setError(null);
    try {
      const base = { name, sku: sku || undefined, isConsumable, attributes: result.value };
      if (editing) await updateCatalogItem(item.id, base);
      else await createCatalogItem({ category, ...base });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title={editing ? 'Edit item' : 'Add item'}
      submitLabel={saving ? 'Saving…' : 'Save'}
      saving={saving}
      onClose={onClose}
      onSubmit={handleSave}
      maxWidth="max-w-lg"
    >
      <ItemFields
        editing={editing}
        category={category}
        name={name}
        sku={sku}
        isConsumable={isConsumable}
        attributes={attributes}
        defs={defs}
        fieldErrors={fieldErrors}
        onCategory={setCategory}
        onName={setName}
        onSku={setSku}
        onConsumable={setIsConsumable}
        onAttributes={setAttributes}
      />
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </ModalShell>
  );
}
